import { isSuperAdmin as _isSuperAdmin } from "@repo/common";
import type { TwoFactorVerificationStatus } from "@repo/db";
import {
  hasUnrestrictedInternalAccess,
  type InternalRole,
  parseViewAs,
  resolveEffectiveInternalRole,
  VIEW_AS_COOKIE_NAME,
  ViewAs,
} from "@repo/db/constants";

// 30-day TTL: long enough for an impersonation session, short enough to
// self-clear if forgotten.
const VIEW_AS_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

// Superseded by the terac-view-as cookie; cleared on every setViewAs so
// admins mid-impersonation on the old system drop back to Admin once.
const LEGACY_VIEW_COOKIES = ["terac-customer-view", "__internal_role_override"];

/**
 * Stored as a cookie (not localStorage) so server code can honor the
 * override too: the study layout guard and the API context read it from
 * request headers before any client code runs.
 */
export function getViewAs(): ViewAs {
  if (typeof document === "undefined") {
    return ViewAs.SUPERADMIN;
  }
  const raw = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(`${VIEW_AS_COOKIE_NAME}=`))
    ?.slice(VIEW_AS_COOKIE_NAME.length + 1);
  return parseViewAs(raw);
}

export function setViewAs(viewAs: ViewAs): void {
  if (typeof document === "undefined") {
    return;
  }
  if (viewAs === ViewAs.SUPERADMIN) {
    document.cookie = `${VIEW_AS_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`;
  } else {
    document.cookie = `${VIEW_AS_COOKIE_NAME}=${viewAs}; path=/; max-age=${VIEW_AS_COOKIE_MAX_AGE}; SameSite=Lax`;
  }
  for (const legacy of LEGACY_VIEW_COOKIES) {
    document.cookie = `${legacy}=; path=/; max-age=0; SameSite=Lax`;
  }
  localStorage.removeItem("terac-customer-view");
}

export function clearViewAs(): void {
  setViewAs(ViewAs.SUPERADMIN);
}

/**
 * Whether to render the admin-flavoured UI, with the client-side view-as
 * override applied. The session carries the caller's real role, so the override
 * has to be resolved here the same way the server resolves it; otherwise a
 * SUPERADMIN previewing as RECRUITER keeps drawing affordances no recruiter has.
 *
 * The CUSTOMER check stays ahead of that resolution because it also has to
 * narrow the email fallback below, which has no role to resolve.
 *
 * Authority comes from `internalRole`, not the email domain, so a restricted
 * role (RECRUITER) is excluded even though it holds a Terac address. The role
 * rides on the session, which is cached, so treat a `true` here as a hint about
 * what to draw. Every gate that matters re-reads the role server-side.
 *
 * Falls back to the email test only when the caller passes a user with no role
 * field at all, which keeps older call sites working rather than silently
 * downgrading a superadmin to a customer view.
 */
export function isSuperAdmin(
  user:
    | {
        email?: string | null;
        emailVerified?: boolean;
        internalRole?: InternalRole | null;
      }
    | null
    | undefined,
): boolean {
  const viewAs = getViewAs();
  if (viewAs === ViewAs.CUSTOMER) {
    return false;
  }
  if (user && "internalRole" in user) {
    const realRole = user.internalRole ?? null;
    if (!realRole) {
      return false;
    }
    return hasUnrestrictedInternalAccess(
      resolveEffectiveInternalRole(realRole, viewAs),
    );
  }
  return _isSuperAdmin(user);
}

// Shared session type definitions to avoid type assertions
export interface ExtendedUser {
  id: string;
  email?: string | null;
  name?: string | null;
  primaryPhoneNumber?: string | null;
  emailVerified?: boolean;
  twoFactorEnabled?: boolean | null;
  internalRole?: InternalRole | null;
}

export interface ExtendedSessionRecord {
  id: string;
  userId: string;
  activeOrganizationId?: string | null;
  twoFactorVerificationStatus?: TwoFactorVerificationStatus;
  impersonatedBy?: string | null;
}

export interface ExtendedSession {
  user: ExtendedUser;
  session?: ExtendedSessionRecord;
}
